import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, MapPin, CheckCircle, Loader } from 'lucide-react';
import { mainAdminApi } from '../../../config';
import LabTestBooking from './LabTestBooking';

interface LabTest {
    _id: string;
    name: string;
    category: string;
    price: number;
    originalPrice: number;
    parameters: string[];
    sampleType: string;
    fastingRequired: boolean;
    reportTime: string;
    description: string;
}

const LabTestDetailPage = () => {
    const { testId } = useParams<{ testId: string }>();
    const navigate = useNavigate();
    const [test, setTest] = useState<LabTest | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showBookingModal, setShowBookingModal] = useState(false);

    useEffect(() => {
        fetchTestDetails();
    }, [testId]);

    const fetchTestDetails = async () => {
        try {
            setLoading(true);
            const response = await mainAdminApi.get<{ labTest: LabTest }>(`/lab-test/${testId}`);
            setTest((response.data as any).labTest);
            setError('');
        } catch (err) {
            console.error('Failed to fetch test details:', err);
            setError('Failed to load test details. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <Loader className="w-8 h-8 text-teal-600 animate-spin" />
            </div>
        );
    }

    if (error || !test) {
        return (
            <div className="min-h-screen bg-gray-50 py-12">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-teal-600 hover:text-teal-700 mb-6"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        वापस जाएं
                    </button>
                    <div className="bg-white rounded-lg shadow-md p-8 text-center">
                        <p className="text-red-600 font-medium">{error || 'टेस्ट नहीं मिला'}</p>
                        <button
                            onClick={() => navigate('/lab-test')}
                            className="mt-4 bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700"
                        >
                            लैब टेस्ट पर वापस जाएं
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
            {/* Header */}
            <section className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 py-16 text-white shadow-lg">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-white/90 hover:text-white mb-8 font-medium transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        वापस
                    </button>
                    <div>
                        <span className="inline-block bg-white/20 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4 backdrop-blur-sm">
                            {test.category}
                        </span>
                        <h1 className="text-5xl font-bold mb-3">{test.name}</h1>
                        <p className="text-lg text-white/90">{test.description}</p>
                    </div>
                </div>
            </section>

            {/* Main Content */}
            <section className="py-12">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Left Column - Details */}
                        <div className="lg:col-span-2">
                            {/* Key Information */}
                            <div className="bg-white rounded-xl shadow-md p-8 mb-6 border border-slate-100">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                                    <span className="w-1 h-8 bg-emerald-600 rounded-full"></span>
                                    टेस्ट की जानकारी
                                </h2>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                    <div className="border-b pb-6 md:border-b-0 md:pb-0">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full flex items-center justify-center shadow-sm">
                                                <MapPin className="w-6 h-6 text-emerald-600" />
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-500">सैंपल प्रकार</p>
                                                <p className="text-lg font-bold text-gray-900">{test.sampleType}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="border-b pb-6 md:border-b-0 md:pb-0">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center shadow-sm">
                                                <CheckCircle className="w-6 h-6 text-amber-600" />
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-500">उपवास</p>
                                                <p className={`text-lg font-bold ${test.fastingRequired ? 'text-amber-600' : 'text-emerald-600'}`}>
                                                    {test.fastingRequired ? 'आवश्यक' : 'आवश्यक नहीं'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Parameters */}
                            <div className="bg-white rounded-xl shadow-md p-8 mb-6 border border-slate-100">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                                    <span className="w-1 h-8 bg-emerald-600 rounded-full"></span>
                                    शामिल पैरामीटर
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {test.parameters && test.parameters.length > 0 ? (
                                        test.parameters.map((param, index) => (
                                            <div key={index} className="flex items-start gap-3 p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg border border-emerald-100 hover:shadow-md transition-shadow">
                                                <CheckCircle className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0" />
                                                <span className="text-gray-700 font-medium">{param}</span>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-gray-600 col-span-full">कोई पैरामीटर सूचीबद्ध नहीं</p>
                                    )}
                                </div>
                            </div>

                            {/* Report Timeline Info */}
                            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border-l-4 border-blue-500 p-6 rounded-xl mb-6 shadow-sm">
                                <h3 className="font-bold text-blue-900 mb-2 flex items-center gap-2">⏱️ रिपोर्ट डिलीवरी समयरेखा</h3>
                                <p className="text-blue-800 text-sm leading-relaxed">
                                    रिपोर्ट सैंपल संग्रह के <strong>24-48 घंटे</strong> के भीतर प्रदान की जाएगी। आप ईमेल और व्हाट्सएप के माध्यम से अपनी रिपोर्ट प्राप्त करेंगे।
                                </p>
                            </div>
                            <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 border-l-4 border-amber-600 rounded-xl p-8 shadow-md border border-amber-200">
                                <div className="flex items-start gap-3 mb-4">
                                    <span className="text-amber-600 text-2xl">⚠️</span>
                                    <p className="font-bold text-amber-900 text-lg">उपवास (फास्टिंग) आवश्यकताएं</p>
                                </div>
                                <p className="text-amber-800 text-base leading-relaxed">
                                    यदि किसी टेस्ट के लिए उपवास आवश्यक है, तो कृपया सैंपल संग्रह से पहले कम से कम <span className="font-semibold">10-12 घंटे</span> का उपवास करें। सटीक परिणामों के लिए यह बहुत महत्वपूर्ण है।
                                </p>
                            </div>

                            {/* Description */}
                            {test.description && (
                                <div className="bg-white rounded-xl shadow-md p-8 border border-slate-100">
                                    <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                                        <span className="w-1 h-8 bg-emerald-600 rounded-full"></span>
                                        इस टेस्ट के बारे में
                                    </h2>
                                    <p className="text-gray-700 leading-relaxed">{test.description}</p>
                                </div>
                            )}
                        </div>

                        {/* Right Column - Pricing & CTA */}
                        <div className="lg:col-span-1">
                            <div className="bg-gradient-to-b from-white to-slate-50 rounded-xl shadow-lg p-8 sticky top-24 border border-slate-100">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">मूल्य निर्धारण</h2>

                                <div className="mb-8 p-6 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg border border-emerald-200">
                                    <div className="flex items-baseline gap-2 mb-3">
                                        <span className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">₹{test.price}</span>
                                        <span className="text-lg text-gray-500 line-through">₹{test.originalPrice}</span>
                                    </div>
                                    <p className="text-sm font-semibold text-emerald-700">
                                        💰 ₹{(test.originalPrice - test.price).toFixed(0)} बचाएं
                                    </p>
                                </div>

                                {/* Features */}
                                <div className="space-y-3 mb-8 pb-8 border-b border-slate-200">
                                    <div className="flex items-center gap-3">
                                        <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                                        <span className="text-sm text-gray-700 font-medium">होम सैंपल कलेक्शन</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                                        <span className="text-sm text-gray-700 font-medium">NABL मान्यता प्राप्त लैब</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                                        <span className="text-sm text-gray-700 font-medium">ऑनलाइन रिपोर्ट उपलब्ध</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                                        <span className="text-sm text-gray-700 font-medium">विशेषज्ञ परामर्श</span>
                                    </div>
                                </div>

                                {/* CTA Buttons */}
                                <div className="space-y-3">
                                    <button
                                        onClick={() => setShowBookingModal(true)}
                                        className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg hover:from-emerald-700 hover:to-teal-700 transition-all duration-200 transform hover:-translate-y-0.5">
                                        ✓ अभी बुक करें
                                    </button>
                                </div>

                                {/* Refund Policy */}
                                <p className="text-xs text-gray-600 mt-6 pt-6 border-t border-slate-200 text-center">
                                    कोई रिफंड नीति नहीं — केवल बुकिंग और रीशेड्यूलिंग उपलब्ध
                                </p>

                                {/* Support Info */}
                                <div className="mt-8 p-5 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg text-center border border-blue-100">
                                    <p className="text-sm text-gray-700 mb-2 font-medium">सहायता चाहिए?</p>
                                    <p className="text-lg font-bold text-emerald-600">+91 8302270668</p>
                                    <p className="text-xs text-gray-600 mt-2">⏰ 24/7 उपलब्ध</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Related Tests Section */}
            <section className="bg-gradient-to-r from-white to-slate-50 py-16 border-t border-slate-200">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-2">
                        <span className="w-1 h-10 bg-emerald-600 rounded-full"></span>
                        अन्य {test.category} टेस्ट
                    </h2>
                    <div className="text-center">
                        <p className="text-gray-600 mb-6 text-lg">अन्य संबंधित परीक्षण उपलब्ध हैं</p>
                        <button
                            onClick={() => navigate('/lab-test')}
                            className="inline-block bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-8 py-3 rounded-lg font-semibold hover:shadow-lg hover:from-emerald-700 hover:to-teal-700 transition-all duration-200 transform hover:-translate-y-0.5"
                        >
                            सभी टेस्ट देखें →
                        </button>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-16 bg-gradient-to-b from-slate-50 to-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-2">
                        <span className="w-1 h-10 bg-emerald-600 rounded-full"></span>
                        अक्सर पूछे जाने वाले प्रश्न
                    </h2>
                    <div className="space-y-4">
                        <details className="border border-slate-200 rounded-xl p-6 bg-white hover:shadow-md transition-shadow group">
                            <summary className="font-semibold text-gray-900 cursor-pointer hover:text-emerald-600 flex items-center gap-2">
                                <span className="text-emerald-600 group-open:rotate-90 transition-transform">▶</span>
                                नमूना कैसे एकत्र किया जाता है?
                            </summary>
                            <p className="mt-4 text-gray-600 leading-relaxed ml-6">
                                हमारे प्रशिक्षित फ़्लेबोटोमिस्ट आपके पसंदीदा समय पर नमूना एकत्र करने के लिए आपके घर जाएंगे।
                            </p>
                        </details>

                        <details className="border border-slate-200 rounded-xl p-6 bg-white hover:shadow-md transition-shadow group">
                            <summary className="font-semibold text-gray-900 cursor-pointer hover:text-emerald-600 flex items-center gap-2">
                                <span className="text-emerald-600 group-open:rotate-90 transition-transform">▶</span>
                                मुझे अपनी रिपोर्ट कब मिलेगी?
                            </summary>
                            <p className="mt-4 text-gray-600 leading-relaxed ml-6">
                                रिपोर्ट आमतौर पर {test.reportTime} के भीतर उपलब्ध होती है। आप इसे ईमेल के माध्यम से प्राप्त करेंगे और कभी भी ऑनलाइन देख सकते हैं।
                            </p>
                        </details>

                        <details className="border border-slate-200 rounded-xl p-6 bg-white hover:shadow-md transition-shadow group">
                            <summary className="font-semibold text-gray-900 cursor-pointer hover:text-emerald-600 flex items-center gap-2">
                                <span className="text-emerald-600 group-open:rotate-90 transition-transform">▶</span>
                                क्या मुझे इस परीक्षण से पहले उपवास करना चाहिए?
                            </summary>
                            <p className="mt-4 text-gray-600 leading-relaxed ml-6">
                                {test.fastingRequired ? 'हाँ, इस परीक्षण के लिए उपवास आवश्यक है। कृपया परीक्षण से 8-12 घंटे पहले भोजन और पानी से बचें।' : 'नहीं, इस परीक्षण के लिए उपवास आवश्यक नहीं है। आप सामान्य रूप से खा और पी सकते हैं।'}
                            </p>
                        </details>

                        <details className="border border-slate-200 rounded-xl p-6 bg-white hover:shadow-md transition-shadow group">
                            <summary className="font-semibold text-gray-900 cursor-pointer hover:text-emerald-600 flex items-center gap-2">
                                <span className="text-emerald-600 group-open:rotate-90 transition-transform">▶</span>
                                क्या मैं अपने परिणामों के बारे में डॉक्टर से परामर्श कर सकता हूँ?
                            </summary>
                            <p className="mt-4 text-gray-600 leading-relaxed ml-6">
                                हाँ, हमारे चिकित्सा विशेषज्ञ परामर्श के लिए उपलब्ध हैं। आप अपने परीक्षण परिणामों पर विचार करने के लिए हमारे डॉक्टरों के साथ परामर्श बुक कर सकते हैं।
                            </p>
                        </details>
                    </div>
                </div>
            </section>

            {/* Booking Modal */}
            {showBookingModal && test && (
                <LabTestBooking
                    test={test}
                    onClose={() => setShowBookingModal(false)}
                    onBookingSuccess={(bookingId) => {
                        // Handle successful booking
                        console.log('Booking successful:', bookingId);
                    }}
                />
            )}
        </div>
    );
};

export default LabTestDetailPage;





